'use client';

import { useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { ArrowLeft, ArrowUpRight, Check, Copy, Mail, MapPin, MoveUpRight, Phone } from 'lucide-react';
import { createPortal } from 'react-dom';
import LiquidGlass from './liquid-glass';
import { readContactResponse } from './contact-response.js';

type Props = { onOpenMap: () => void; onPrefetchMap: () => void };

export default function ContactBoard({ onOpenMap, onPrefetchMap }: Props) {
  const [view, setView] = useState<'details' | 'form'>('details');
  const [phone, setPhone] = useState<{ label: string; international: string } | null>(null);
  const [copyStatus, setCopyStatus] = useState('');
  const [phoneClosing, setPhoneClosing] = useState(false);
  function closePhoneDialog() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) phoneDialog.current?.close();
    else setPhoneClosing(true);
  }
  const phoneDialog = useRef<HTMLDialogElement>(null);
  const phoneHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (!phone) return;
    phoneDialog.current?.showModal();
    // Announce the dialog without selecting its first action (the close button).
    phoneHeading.current?.focus({ preventScroll: true });
  }, [phone]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const nameInput = useRef<HTMLInputElement>(null);
  const startButton = useRef<HTMLButtonElement>(null);
  const success = useRef<HTMLOutputElement>(null);
  const previousView = useRef(view);
  const inFlight = useRef(false);
  const board = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const switching = useRef(false);

  async function changeView(next: 'details' | 'form') {
    if (switching.current || next === view) return;
    const panel = board.current;
    const inner = content.current;
    const mobile = matchMedia('(pointer: coarse), (max-width: 700px)').matches;
    if (panel && !mobile) panel.dataset.contactFocus = next;
    if (!panel || !inner || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (panel) panel.dataset.contactFocus = next;
      setView(next);
      return;
    }
    switching.current = true;
    // Pin the panel before React swaps views on every screen. Its observer
    // keeps the WebGL bezel aligned with the interpolated DOM height.
    const previousHeight = panel.style.height;
    const previousOverflow = panel.style.overflow;
    const fromHeight = panel.offsetHeight;
    const panelStyle = getComputedStyle(panel);
    const verticalFrame = parseFloat(panelStyle.paddingTop) + parseFloat(panelStyle.paddingBottom)
      + parseFloat(panelStyle.borderTopWidth) + parseFloat(panelStyle.borderBottomWidth);
    let resize: Animation | undefined;
    panel.style.height = `${fromHeight}px`;
    panel.style.overflow = 'hidden';
    panel.dataset.transitioning = 'true';
    const fadeOut = inner.animate([{ opacity: 1, transform: 'translateX(0)' }, { opacity: 0, transform: mobile ? 'translateX(24px)' : 'none' }], { duration: mobile ? 220 : 120, easing: mobile ? 'cubic-bezier(.4,0,.6,1)' : 'ease-in', fill: 'forwards' });
    try {
      await fadeOut.finished;
      if (!panel.isConnected) return;
      setView(next);
      await new Promise<void>(resolve => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
      if (!panel.isConnected) return;
      {
        const toHeight = inner.offsetHeight + verticalFrame;
        panel.style.height = `${toHeight}px`;
        // Fit the camera to the final size, rather than chasing each resize frame.
        panel.dataset.contactTargetHeight = String(toHeight);
        if (mobile) {
          // Apply the auto-height while contents are invisible. Scaling the
          // whole board vertically made the horizontal reveal look like a bounce.
          panel.dataset.contactFocus = next;
          // Let the existing camera fit settle before revealing the new content.
          await new Promise<void>(resolve => window.setTimeout(resolve, 520));
        } else {
          resize = panel.animate([{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
            { duration: 460, easing: 'cubic-bezier(.4,0,.2,1)' });
        }
        panel.dataset.contactFocus = next;
      }
      fadeOut.cancel();
      const fadeIn = inner.animate([{ opacity: 0, transform: mobile ? 'translateX(-24px)' : 'translateY(8px)' }, { opacity: 1, transform: mobile ? 'translateX(0)' : 'translateY(0)' }], { duration: mobile ? 520 : 460, easing: mobile ? 'cubic-bezier(.22,1,.36,1)' : 'cubic-bezier(.4,0,.2,1)' });
      await Promise.all([fadeIn.finished, resize?.finished]);
      if (next === 'details') startButton.current?.focus({ preventScroll: true });
      else if (!matchMedia('(pointer: coarse), (max-width: 700px)').matches)
        nameInput.current?.focus({ preventScroll: true });
    } finally {
      fadeOut.cancel();
      resize?.cancel();
      panel.style.height = previousHeight;
      panel.style.overflow = previousOverflow;
      delete panel.dataset.contactResize;
      delete panel.dataset.contactTargetHeight;
      delete panel.dataset.transitioning;
      switching.current = false;
    }
  }

  useEffect(() => {
    if (view === 'form' && sent) {
      success.current?.focus({ preventScroll: true });
    } else if (previousView.current !== view && !switching.current) {
      if (view === 'details') startButton.current?.focus({ preventScroll: true });
      else if (!matchMedia('(pointer: coarse), (max-width: 700px)').matches) nameInput.current?.focus({ preventScroll: true });
    }
    previousView.current = view;
  }, [view, sent]);

  async function sendEnquiry(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    const form = event.currentTarget;
    const data = new FormData(form);
    setSending(true);
    setError('');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch('/api/contact', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.get('name'), email: data.get('email'), company: data.get('company'), project: data.get('project') }),
        signal: controller.signal,
      });
      await readContactResponse(response);
      form.reset();
      setSent(true);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Unable to send your enquiry. Please try again.');
    } finally {
      window.clearTimeout(timeout);
      inFlight.current = false;
      setSending(false);
    }
  }

  return (
    <>
    <div ref={board} className="contact-inner contact-reception" data-contact-view={view}>
      <div ref={content}>
      <div className="contact-board-topline">
        {<button type="button" hidden={view !== 'form'} className="contact-board-back glass-surface" onClick={() => void changeView('details')}><LiquidGlass /><ArrowLeft size={14} aria-hidden="true" /><span>Back</span></button>}
      </div>
      <h2 id="contact-title" hidden={view === 'form'}>Contact Us<span>.</span></h2>
      {/* Inactive views stay laid out off-flow so first opening does not build the form layout. */}
      <div className="contact-board-views">
        <div className={`contact-board-view contact-board-details ${view === 'details' ? 'is-active' : ''}`} inert={view !== 'details'} aria-hidden={view !== 'details'}>
          <div className="contact-directory">
            <div className="contact-directory-row">
              <Mail size={21} strokeWidth={1.5} aria-hidden="true" />
              <div className="contact-directory-info"><span className="contact-directory-label">Email</span><a href="mailto:info@codexsolutions.io">info@codexsolutions.io</a></div>
              <ArrowUpRight size={17} strokeWidth={1.5} aria-hidden="true" className="contact-directory-arrow" />
            </div>
            <div className="contact-directory-row">
              <Phone size={21} strokeWidth={1.5} aria-hidden="true" />
              <div className="contact-directory-info"><span className="contact-directory-label">Call us</span><div className="contact-directory-phones">{[{label:'0770 801 8007',international:'9647708018007'},{label:'0750 108 8007',international:'9647501088007'}].map(number => <button type="button" key={number.international} aria-haspopup="dialog" onClick={() => { setCopyStatus(''); setPhone(number); }}>{number.label}</button>)}</div></div>
            </div>
            <div className="contact-directory-row">
              <MapPin size={21} strokeWidth={1.5} aria-hidden="true" />
              <div className="contact-directory-info"><span className="contact-directory-label">Visit us</span><button type="button" className="contact-location" onPointerEnter={onPrefetchMap} onFocus={onPrefetchMap} onClick={onOpenMap}>Baxtyari - New Baxtyari Street <ArrowUpRight size={17} strokeWidth={1.5} aria-hidden="true" /></button></div>
            </div>
          </div>
          <button ref={startButton} type="button" className="contact-reception-cta" onClick={() => void changeView('form')}><span>Start a conversation</span><MoveUpRight size={19} /></button>
        </div>
        <div className={`contact-board-view contact-board-enquiry ${view === 'form' ? 'is-active' : ''}`} inert={view !== 'form'} aria-hidden={view !== 'form'}>
          <form className={`contact-board-form ${sent ? 'is-sent' : ''}`} aria-label="Contact enquiry" inert={sent} aria-hidden={sent} onSubmit={sendEnquiry} data-native-scroll>
            <div className="contact-board-fields">
              <label htmlFor="contact-name"><input aria-label="Your name" ref={nameInput} id="contact-name" name="name" autoComplete="name" placeholder="Full name" required maxLength={100} /></label>
              <label htmlFor="contact-email"><input aria-label="Work email" id="contact-email" name="email" type="email" autoComplete="email" placeholder="you@company.com" required maxLength={200} /></label>
            </div>
            <label htmlFor="contact-company"><input aria-label="Company (optional)" id="contact-company" name="company" autoComplete="organization" placeholder="Company name (optional)" maxLength={150} /></label>
            <label htmlFor="contact-project"><textarea aria-label="What do you have in mind?" id="contact-project" name="project" rows={2} placeholder="Tell us about your project…" required maxLength={5000} /></label>
            <button className="contact-reception-cta" type="submit" disabled={sending}><span>{sending ? 'Sending…' : 'Send enquiry'}</span><Mail size={17} aria-hidden="true" /></button>
            <output className="contact-board-error" aria-live="polite">{error}</output>
          </form>
          {sent && <output ref={success} className="contact-board-success" tabIndex={-1}>
            <Check size={32} aria-hidden="true" />
            <h3>Your enquiry has been sent.</h3>
            <p>Thank you. Our team will reply to the email address you provided.</p>
            <button type="button" className="contact-reception-cta" onClick={() => { setSent(false); setError(''); requestAnimationFrame(() => nameInput.current?.focus({ preventScroll: true })); }}>Send another enquiry<ArrowUpRight size={17} /></button>
          </output>}
        </div>
      </div>
      </div>
    </div>
    {phone && createPortal(<dialog ref={phoneDialog} className={`phone-options-dialog${phoneClosing ? ' is-closing' : ''}`} aria-labelledby="phone-options-title" onCancel={event => { event.preventDefault(); closePhoneDialog(); }} onAnimationEnd={event => { if (event.target === event.currentTarget && event.animationName === 'phone-dialog-exit') phoneDialog.current?.close(); }} onClose={() => { setPhone(null); setPhoneClosing(false); }} data-native-scroll>
      <div className="phone-options-heading"><h2 ref={phoneHeading} tabIndex={-1} id="phone-options-title">{phone.label}</h2><button type="button" aria-label="Close phone options" onClick={closePhoneDialog}>×</button></div>
      <p>Choose how to get in touch.</p>
      <a href={`tel:+${phone.international}`}><Phone size={18} />Call phone number</a>
      <a href={`https://wa.me/${phone.international}`} target="_blank" rel="noopener noreferrer"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.52 3.48A11.9 11.9 0 0 0 12.05 0C5.47 0 .12 5.35.12 11.93c0 2.1.55 4.15 1.6 5.96L.02 24l6.25-1.64a11.9 11.9 0 0 0 5.78 1.47h.01c6.58 0 11.93-5.35 11.93-11.93 0-3.19-1.24-6.18-3.47-8.42ZM12.06 21.8a9.86 9.86 0 0 1-5.03-1.38l-.36-.21-3.71.97.99-3.62-.24-.37a9.85 9.85 0 0 1-1.51-5.26c0-5.46 4.45-9.91 9.91-9.91a9.85 9.85 0 0 1 7 2.9 9.84 9.84 0 0 1 2.9 7c0 5.46-4.44 9.88-9.95 9.88Zm5.44-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.61-.91-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.87 1.21 3.07c.15.2 2.1 3.2 5.08 4.48.71.31 1.27.49 1.7.63.71.22 1.36.19 1.87.11.57-.09 1.76-.72 2.01-1.41.25-.69.25-1.28.17-1.41-.07-.12-.27-.2-.57-.35Z" /></svg>WhatsApp</a>
      <button type="button" className="phone-copy" onClick={async () => { try { await navigator.clipboard.writeText(`+${phone.international}`); setCopyStatus('Phone number copied'); } catch { setCopyStatus('Unable to copy. Please copy the number shown above.'); } }}><Copy size={18} aria-hidden="true" />Copy phone number</button>
      <output aria-live="polite">{copyStatus}</output>
    </dialog>, document.body)}
    </>
  );
}
